import { FC, ReactNode, useContext, useEffect, useReducer, useRef, MutableRefObject, useState } from 'react'
import { BookingContext } from '../booking'
import { RtcSession } from './types'
import { ConferenceContext, initialState } from './ConferenceContext'
import { reducer } from './ConferenceReducer'
import { ZoomClientInitializerParams, ZoomRTCProviderId } from 'zoom'
import { ExceptionType, GOCException } from 'exceptions'
import { initializePrimusClient } from 'primus'
import { User } from 'api/types'
import { setBreakoutFaroClassId } from 'services/breakoutFaroLogger'

interface setTimerParams {
    (callback: () => void, startAfter: number): void
}

const useTimer = (): { setTimer: setTimerParams; timerId: MutableRefObject<NodeJS.Timeout | undefined> } => {
    const timerId = useRef<NodeJS.Timeout>()

    const setTimer: setTimerParams = (callback, startAfter = 0) => {
        timerId.current = setTimeout(() => {
            callback()
        }, startAfter * 1000)
    }

    return { timerId, setTimer }
}

const useInterval = (): { intervalHandler: setTimerParams; intervalId: MutableRefObject<NodeJS.Timeout | undefined> } => {
    const intervalId = useRef<NodeJS.Timeout>()

    const intervalHandler = (callback: () => void, interval: number): void => {
        intervalId.current = setInterval(callback, interval)
    }

    return { intervalId, intervalHandler }
}

const Conference: FC<{ children: ReactNode }> = ({ children }) => {
    const [conferenceVideoPlayer, setConferenceVideoPlayer] = useState<VideoPlayerContainer | null>(null)
    const booking = useContext(BookingContext)
    const classroomId = useRef<string>()
    const rtcSession = useRef<RtcSession>()
    const auth = useRef<string>()
    const currentUser = useRef<User>()
    const { timerId, setTimer } = useTimer()
    const { intervalId, intervalHandler } = useInterval()
    // Canvas state removed - Zoom SDK 2.x uses video elements instead

    const setTimerInterval = (): void => {
        const updateTimeLeft = (): void => {
            dispatch({ method: 'update-time-left' })
        }

        dispatch({ method: 'check-time-left' })

        intervalHandler(updateTimeLeft, 60000)
    }

    const setSessionTimer = (timeLeft: number): void => {
        const timeInMinutes = Math.floor(timeLeft / 60)
        const startAfter = timeLeft % 60

        dispatch({ method: 'set-time-left', timeLeft: timeInMinutes })
        setTimer(setTimerInterval, startAfter)
    }

    const [state, dispatch] = useReducer(reducer({ auth, classroomId, rtcSession, currentUser, setConferenceVideoPlayer }), {
        ...initialState,

        setSlideAnnotation: slideAnnotations => {
            if (slideAnnotations) {
                dispatch({ method: 'update-slide-annotations', slideAnnotations })
            }
        },
        setWhiteBoardAnnotation: whiteBoardAnnotations => {
            if (whiteBoardAnnotations) {
                dispatch({ method: 'update-whiteboard-annotations', whiteBoardAnnotations })
            }
        },
        setClassResults: isViewed => {
            dispatch({ method: 'update-class-result', isViewed })
        },
        setCueCardInfo: cueCardInfo => {
            if (cueCardInfo) {
                dispatch({ method: 'update-cue-card-data', cueCardInfo })
            }
        },
        setCueCardForStudent: cueCard => {
            if (cueCard) {
                dispatch({ method: 'update-student-cue-card', cueCard })
            }
        },
        setStudentAwayStatus: studentAwayStatus => {
            if (studentAwayStatus) {
                dispatch({ method: 'update-user-away', studentAwayStatus })
            }
        },
        setRetryError: viewRetryErrorSnackbar => {
            if (viewRetryErrorSnackbar) {
                dispatch({ method: 'set-retry-error', viewRetryErrorSnackbar })
            }
        },
        setVideoSize: (size, videoContainer) => {
            dispatch({ method: 'set-video-player-size', size, videoPlayer: videoContainer })
        }
    })

    useEffect(() => {
        if (booking.status === 'validated' && state.status === 'waiting-for-classroom-info' && conferenceVideoPlayer) {
            dispatch({ method: 'set-status', status: 'initializing-client' })
            classroomId.current = booking.classroomId
            setBreakoutFaroClassId(booking.classroomId)
            auth.current = booking.auth
            currentUser.current = booking.check.user
            const userId = booking.check.user.id
            const userRole = booking.check.user.role

            window.clarity('identify', userId, booking.check.session[ZoomRTCProviderId].topic)

            window.clarity('set', 'User Id', userId)
            window.clarity('set', 'Firstname', booking.check.user.firstName)
            window.clarity('set', 'Lastname', booking.check.user.lastName)
            window.clarity('set', 'Session Details', booking.check.session[ZoomRTCProviderId].topic)

            void Promise.all([
                new Promise<ZoomClientInitializerParams>((resolve, reject) => {
                    if (booking.check.session.rtcServiceProvider === ZoomRTCProviderId) {
                        resolve({
                            dispatch,
                            topic: booking.check.session[ZoomRTCProviderId].topic,
                            auth: booking.auth,
                            wseEncodedUserInfo: {
                                fullName: `${booking.check.user.firstName} ${booking.check.user.lastName}`.substring(0, 16),
                                role: userRole,
                                id: userId,
                                micHardMuted: booking.check.user.micHardMuted
                            }
                        })

                    } else {
                        reject(new GOCException(ExceptionType.RtcClientNotSupported))
                    }
                }),
                initializePrimusClient({ classroomId: booking.classroomId, userId, userRole })
            ]).then(([clientParams, primus]) => {
                dispatch({
                    method: 'ready-to-join',
                    clientParams,
                    primus,
                    setSessionTimer
                })
            })
        }
    }, [booking.status, state.status, conferenceVideoPlayer])

    useEffect(() => {
        if (state.status === 'session-joined' && state.classroomStateOnJoin) {
            const studyPlace = state.classroomStateOnJoin.StudyPlace
            if (studyPlace && window.growthbook) {
                window.growthbook?.setAttributes({
                    ...window.growthbook?.getAttributes(),
                    centerRefId: studyPlace
                })
            }
        }
    }, [state.status])

    useEffect(() => {
        return () => {
            clearTimeout(timerId.current)
            clearInterval(intervalId.current)
        }
    }, [])

    return (
        <ConferenceContext.Provider value={state}>
            {children}
        </ConferenceContext.Provider>
    )
}

export { Conference }
